/**
 * API Service Layer - Firebase Implementation
 * Uses Firebase Firestore and Authentication for all data operations.
 */

import {
    collection,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    where,
    orderBy,
    limit,
    setDoc,
    Timestamp,
    onSnapshot
} from 'firebase/firestore';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { db, auth } from '../lib/firebase';

// Helper to mimic the previous fetch response structure
const createFakeResponse = (data, ok = true) => ({
    ok,
    json: async () => data,
    status: ok ? 200 : 400
});

// Helper to convert Firestore doc to object with ID
const docToObj = (docSn) => {
    if (!docSn.exists()) return null;
    const data = docSn.data();
    // Convert Timestamps to ISO strings to match previous API
    for (const key in data) {
        if (data[key] instanceof Timestamp) {
            data[key] = data[key].toDate().toISOString();
        }
    }
    return { ...data, id: docSn.id, _id: docSn.id }; // Support both id formats just in case
};

// ==================== AUTH API ====================

// Monitor auth state to keep local state in sync if needed
// This is a bit looser than the previous strict token check but works better for Firebase
let currentUser = null;
onAuthStateChanged(auth, (user) => {
    currentUser = user;
});

export const authAPI = {
    register: async (userData) => {
        try {
            const { email, password, ...otherData } = userData;
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Assign admin role to specific email, otherwise default to user
            const roles = email === 'admin@premierfinance.lk' ? ['admin'] : ['user'];

            // Try to create user document in Firestore (may fail if rules are locked)
            try {
                await setDoc(doc(db, 'users', user.uid), {
                    email: user.email,
                    ...otherData,
                    roles: roles,
                    createdAt: new Date().toISOString()
                });
            } catch (firestoreError) {
                console.warn("Could not save user profile to Firestore (rules may be locked):", firestoreError.message);
                // Continue anyway - the user is created in Firebase Auth
            }

            return createFakeResponse({ message: 'Registration successful' });
        } catch (error) {
            console.error("Registration error", error);
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    login: async (email, password) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            // We return tokens structure to satisfy the context though we don't strictly use them this way in Firebase
            // The context immediately calls getMe(), which is where the real data comes from.
            return createFakeResponse({
                access_token: "firebase-token-placeholder",
                refresh_token: "firebase-refresh-placeholder"
            });
        } catch (error) {
            console.error("Login error", error);
            // Return error in format expected by AuthContext
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    logout: async () => {
        await signOut(auth);
        return createFakeResponse({ message: 'Logged out' });
    },

    getMe: async () => {
        if (!auth.currentUser) {
            return createFakeResponse({ detail: 'Not authenticated' }, false);
        }

        try {
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (userDoc.exists()) {
                const userData = docToObj(userDoc);
                return createFakeResponse(userData);
            } else {
                // Fallback if no user doc exists - use Firebase Auth data
                // Assign admin role if email matches
                const isAdmin = auth.currentUser.email === 'admin@premierfinance.lk';
                return createFakeResponse({
                    id: auth.currentUser.uid,
                    email: auth.currentUser.email,
                    roles: isAdmin ? ['admin'] : ['user']
                });
            }
        } catch (error) {
            // Firestore may be locked - still return user data from Firebase Auth
            console.warn("Could not read user profile from Firestore:", error.message);
            const isAdmin = auth.currentUser.email === 'admin@premierfinance.lk';
            return createFakeResponse({
                id: auth.currentUser.uid,
                email: auth.currentUser.email,
                roles: isAdmin ? ['admin'] : ['user']
            });
        }
    },

    isAuthenticated: () => {
        return !!auth.currentUser;
    },
};

// ==================== PRODUCTS API ====================

export const productsAPI = {
    getAll: async (params = {}) => {
        try {
            let q = query(collection(db, 'products'));

            if (params.category) {
                q = query(q, where('category', '==', params.category));
            }
            if (params.active_only) {
                q = query(q, where('is_active', '==', true));
            }

            const snapshot = await getDocs(q);
            const products = snapshot.docs.map(docToObj);

            return createFakeResponse(products);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    getBySlug: async (slug) => {
        try {
            const q = query(collection(db, 'products'), where('slug', '==', slug));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                return createFakeResponse({ detail: "Product not found" }, false);
            }

            return createFakeResponse(docToObj(snapshot.docs[0]));
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    create: async (productData) => {
        try {
            const docRef = await addDoc(collection(db, 'products'), {
                ...productData,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            });
            const newDoc = await getDoc(docRef);
            return createFakeResponse(docToObj(newDoc));
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    update: async (slug, productData) => {
        try {
            // First find the doc by slug
            const q = query(collection(db, 'products'), where('slug', '==', slug));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                return createFakeResponse({ detail: "Product not found" }, false);
            }

            const docRef = snapshot.docs[0].ref;
            await updateDoc(docRef, {
                ...productData,
                updated_at: new Date().toISOString()
            });

            const updatedDoc = await getDoc(docRef);
            return createFakeResponse(docToObj(updatedDoc));
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    delete: async (slug) => {
        try {
            const q = query(collection(db, 'products'), where('slug', '==', slug));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                return createFakeResponse({ detail: "Product not found" }, false);
            }

            await deleteDoc(snapshot.docs[0].ref);
            return createFakeResponse({ message: "Deleted successfully" });
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    /**
     * Subscribe to real-time product updates
     * @param {Function} callback - Called with array of products when data changes
     * @param {Object} params - Filter parameters
     * @returns {Function} Unsubscribe function
     */
    subscribeToProducts: (callback, params = {}) => {
        try {
            let q = query(collection(db, 'products'));
            if (params.category) {
                q = query(q, where('category', '==', params.category));
            }
            if (params.active_only) {
                q = query(q, where('is_active', '==', true));
            }

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const products = snapshot.docs.map(docToObj);
                console.log('🔄 Real-time products update:', products.length);
                callback(products);
            }, (error) => {
                console.error('Products subscription error:', error);
                // Fallback to static fetch if listener fails (e.g. permission)
                productsAPI.getAll(params).then(res => res.json()).then(callback);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Failed to subscribe to products:', error);
            return () => { };
        }
    }
};

// ==================== ADMIN API ====================

export const adminAPI = {
    getDashboardStats: async () => {
        try {
            const [productsSn, newsSn, contactsSn, unreadContactsSn] = await Promise.all([
                getDocs(query(collection(db, 'products'), where('is_active', '==', true))),
                getDocs(query(collection(db, 'news'), where('is_published', '==', true))),
                getDocs(collection(db, 'contact_submissions')),
                getDocs(query(collection(db, 'contact_submissions'), where('is_read', '==', false)))
            ]);

            return createFakeResponse({
                products: productsSn.size,
                news: newsSn.size,
                contacts: contactsSn.size,
                unread_contacts: unreadContactsSn.size,
                tenders: 0
            });
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    }
};

// ==================== NEWS API ====================
// Saves to Firebase Firestore as primary database
// Also saves to localStorage as backup/cache

const NEWS_STORAGE_KEY = 'siteNews';

// Helper to get news from localStorage
const getLocalNews = () => {
    try {
        const stored = localStorage.getItem(NEWS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Error reading local news:', e);
        return [];
    }
};

// Helper to save news to localStorage
const saveLocalNews = (news) => {
    try {
        localStorage.setItem(NEWS_STORAGE_KEY, JSON.stringify(news));
        return true;
    } catch (e) {
        console.error('Error saving local news:', e);
        return false;
    }
};

export const newsAPI = {
    getAll: async (params = {}) => {
        try {
            let allNews = [];

            // Try Firestore first (primary database)
            try {
                let q;
                if (params.published_only) {
                    q = query(collection(db, 'news'), where('is_published', '==', true), orderBy('created_at', 'desc'));
                } else {
                    q = query(collection(db, 'news'), orderBy('created_at', 'desc'));
                }
                if (params.limit) {
                    q = query(q, limit(parseInt(params.limit)));
                }
                const snapshot = await getDocs(q);
                allNews = snapshot.docs.map(docToObj);
                console.log('✅ Loaded', allNews.length, 'news articles from Firestore');

                // Update localStorage cache
                saveLocalNews(allNews);
            } catch (firestoreError) {
                console.warn('⚠️ Firestore read failed, using localStorage:', firestoreError.message);
                // Fallback to localStorage
                allNews = getLocalNews();
                if (params.published_only) {
                    allNews = allNews.filter(n => n.is_published);
                }
            }

            // Sort by date
            allNews.sort((a, b) => {
                const dateA = new Date(a.created_at || a.date || 0);
                const dateB = new Date(b.created_at || b.date || 0);
                return dateB - dateA;
            });

            // Apply limit
            if (params.limit) {
                allNews = allNews.slice(0, parseInt(params.limit));
            }

            return createFakeResponse(allNews);
        } catch (error) {
            console.error("News getAll error:", error);
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    getBySlug: async (slug) => {
        try {
            // Try Firestore first
            try {
                const q = query(collection(db, 'news'), where('slug', '==', slug));
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    return createFakeResponse(docToObj(snapshot.docs[0]));
                }
            } catch (firestoreError) {
                console.warn('Firestore read failed:', firestoreError.message);
            }

            // Fallback to localStorage
            const localNews = getLocalNews();
            const localArticle = localNews.find(n => n.slug === slug);
            if (localArticle) {
                return createFakeResponse(localArticle);
            }

            return createFakeResponse({ detail: "Article not found" }, false);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    create: async (newsData) => {
        try {
            const newArticle = {
                ...newsData,
                id: Date.now().toString(),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            // Save to Firestore (primary database)
            try {
                const docRef = await addDoc(collection(db, 'news'), newArticle);
                newArticle.firestore_id = docRef.id;
                console.log('✅ News saved to Firestore with ID:', docRef.id);
            } catch (firestoreError) {
                console.error('❌ Firestore write failed:', firestoreError.message);
                console.error('Full error:', firestoreError);
                // Continue to save locally as backup
            }

            // Also save to localStorage (backup/cache)
            const localNews = getLocalNews();
            localNews.unshift(newArticle);
            saveLocalNews(localNews);
            console.log('✅ News saved to localStorage');

            return createFakeResponse(newArticle);
        } catch (error) {
            console.error('News create error:', error);
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    update: async (slug, newsData) => {
        try {
            const updateData = {
                ...newsData,
                updated_at: new Date().toISOString()
            };

            // Update in Firestore (primary)
            try {
                const q = query(collection(db, 'news'), where('slug', '==', slug));
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    await updateDoc(snapshot.docs[0].ref, updateData);
                    console.log('✅ News updated in Firestore');
                }
            } catch (firestoreError) {
                console.error('❌ Firestore update failed:', firestoreError.message);
            }

            // Also update localStorage
            const localNews = getLocalNews();
            const index = localNews.findIndex(n => n.slug === slug);
            if (index !== -1) {
                localNews[index] = { ...localNews[index], ...updateData };
                saveLocalNews(localNews);
                console.log('✅ News updated in localStorage');
            }

            return createFakeResponse(localNews[index] || updateData);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    delete: async (slug) => {
        try {
            // Delete from Firestore (primary)
            try {
                const q = query(collection(db, 'news'), where('slug', '==', slug));
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    await deleteDoc(snapshot.docs[0].ref);
                    console.log('✅ News deleted from Firestore');
                }
            } catch (firestoreError) {
                console.error('❌ Firestore delete failed:', firestoreError.message);
            }

            // Also delete from localStorage
            let localNews = getLocalNews();
            localNews = localNews.filter(n => n.slug !== slug);
            saveLocalNews(localNews);
            console.log('✅ News deleted from localStorage');

            return createFakeResponse({ message: "Deleted successfully" });
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    /**
     * Subscribe to real-time news updates
     * @param {Function} callback - Called with array of news articles when data changes
     * @param {Object} params - Filter parameters (published_only, limit)
     * @returns {Function} Unsubscribe function
     */
    subscribeToNews: (callback, params = {}) => {
        let isSubscribed = true;
        let unsubscribe = () => { };

        const processSnapshot = (docs, source = 'Firebase') => {
            if (!isSubscribed) return;
            try {
                const news = docs.map(docSnap => {
                    const data = docSnap.data();
                    const processedData = { ...data };
                    for (const key in processedData) {
                        if (processedData[key] instanceof Timestamp) {
                            processedData[key] = processedData[key].toDate().toISOString();
                        }
                    }
                    return { ...processedData, id: docSnap.id, __source: source };
                });

                console.log(`📡 News loaded from ${source}:`, news.length);
                saveLocalNews(news);
                callback(news);
            } catch (err) {
                console.error(`❌ Error processing news from ${source}:`, err);
            }
        };

        try {
            let q;
            const newsRef = collection(db, 'news');
            
            // Build query based on params
            if (params.published_only) {
                q = query(newsRef, where('is_published', '==', true), orderBy('created_at', 'desc'));
            } else {
                q = query(newsRef, orderBy('created_at', 'desc'));
            }
            
            if (params.limit) {
                q = query(q, limit(parseInt(params.limit)));
            }

            // Start real-time listener
            unsubscribe = onSnapshot(q, (snapshot) => {
                if (!isSubscribed) return;
                const source = snapshot.metadata.fromCache ? 'Firebase (Cache)' : 'Firebase (Server)';
                processSnapshot(snapshot.docs, source);
            }, (error) => {
                if (!isSubscribed) return;
                console.warn('⚠️ News subscription restricted, falling back to LocalStorage:', error.code);
                
                // Fallback to local storage
                let localNews = getLocalNews().map(item => ({ ...item, __source: 'LocalStorage Fallback' }));
                if (params.published_only) {
                    localNews = localNews.filter(n => n.is_published);
                }
                if (params.limit) {
                    localNews = localNews.slice(0, parseInt(params.limit));
                }
                
                console.log('📦 LocalStorage fallback news:', localNews.length);
                callback(localNews);
            });
        } catch (error) {
            console.error('❌ Failed to setup news subscription:', error);
            callback(getLocalNews());
        }

        return () => {
            isSubscribed = false;
            if (unsubscribe) unsubscribe();
        };
    }
};

// ==================== ADS API ====================
// Real-time ad management with Firebase Firestore

const ADS_STORAGE_KEY = 'siteAds';

// Helper to get ads from localStorage
const getLocalAds = () => {
    try {
        const stored = localStorage.getItem(ADS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Error reading local ads:', e);
        return [];
    }
};

// Helper to save ads to localStorage
const saveLocalAds = (ads) => {
    try {
        localStorage.setItem(ADS_STORAGE_KEY, JSON.stringify(ads));
        return true;
    } catch (e) {
        console.error('Error saving local ads:', e);
        return false;
    }
};

export const adsAPI = {
    getAll: async () => {
        try {
            let allAds = [];

            try {
                const q = query(collection(db, 'ads'), orderBy('created_at', 'desc'));
                const snapshot = await getDocs(q);
                allAds = snapshot.docs.map(docToObj);
                console.log('✅ Loaded', allAds.length, 'ads from Firestore');
                saveLocalAds(allAds);
            } catch (firestoreError) {
                console.warn('⚠️ Firestore ads read failed:', firestoreError.message);
                allAds = getLocalAds();
            }

            return createFakeResponse(allAds);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    getActive: async () => {
        try {
            let activeAds = [];

            try {
                const q = query(collection(db, 'ads'), where('active', '==', true));
                const snapshot = await getDocs(q);
                activeAds = snapshot.docs.map(docToObj);
            } catch (firestoreError) {
                console.warn('⚠️ Firestore ads read failed:', firestoreError.message);
                activeAds = getLocalAds().filter(ad => ad.active);
            }

            return createFakeResponse(activeAds);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    create: async (adData) => {
        try {
            const newAd = {
                ...adData,
                id: Date.now().toString(),
                created_at: new Date().toISOString()
            };

            try {
                const docRef = await addDoc(collection(db, 'ads'), newAd);
                newAd.firestore_id = docRef.id;
                console.log('✅ Ad saved to Firestore');
            } catch (firestoreError) {
                console.error('❌ Firestore ad write failed:', firestoreError.message);
            }

            // Also save to localStorage as backup
            const localAds = getLocalAds();
            localAds.unshift(newAd);
            saveLocalAds(localAds);

            return createFakeResponse(newAd);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    update: async (id, adData) => {
        try {
            const updateData = {
                ...adData,
                updated_at: new Date().toISOString()
            };

            try {
                const docRef = doc(db, 'ads', id);
                await updateDoc(docRef, updateData);
                console.log('✅ Ad updated in Firestore');
            } catch (firestoreError) {
                console.error('❌ Firestore ad update failed:', firestoreError.message);
            }

            // Also update localStorage
            const localAds = getLocalAds();
            const index = localAds.findIndex(a => a.id === id || a.firestore_id === id);
            if (index !== -1) {
                localAds[index] = { ...localAds[index], ...updateData };
                saveLocalAds(localAds);
            }

            return createFakeResponse(updateData);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    delete: async (id) => {
        try {
            try {
                await deleteDoc(doc(db, 'ads', id));
                console.log('✅ Ad deleted from Firestore');
            } catch (firestoreError) {
                console.error('❌ Firestore ad delete failed:', firestoreError.message);
            }

            // Also delete from localStorage
            let localAds = getLocalAds();
            localAds = localAds.filter(a => a.id !== id && a.firestore_id !== id);
            saveLocalAds(localAds);

            return createFakeResponse({ message: "Deleted successfully" });
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    /**
     * Subscribe to real-time ad updates
     * @param {Function} callback - Called with array of active ads when data changes
     * @param {boolean} activeOnly - If true, only return active ads
     * @returns {Function} Unsubscribe function
     */
    subscribeToAds: (callback, activeOnly = true) => {
        try {
            let q;
            if (activeOnly) {
                q = query(collection(db, 'ads'), where('active', '==', true));
            } else {
                q = query(collection(db, 'ads'), orderBy('created_at', 'desc'));
            }

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const ads = snapshot.docs.map(docSnap => {
                    const data = docSnap.data();
                    for (const key in data) {
                        if (data[key] instanceof Timestamp) {
                            data[key] = data[key].toDate().toISOString();
                        }
                    }
                    return { ...data, id: docSnap.id, firestore_id: docSnap.id };
                });
                console.log('🔄 Real-time ads update:', ads.length, 'ads');
                callback(ads);
            }, (error) => {
                console.error('Ads subscription error:', error);
                // Fallback to localStorage
                const localAds = getLocalAds();
                callback(activeOnly ? localAds.filter(a => a.active) : localAds);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Failed to subscribe to ads:', error);
            return () => { };
        }
    }
};

// ==================== CONTACT API ====================

export const contactAPI = {
    submit: async (contactData) => {
        try {
            const docRef = await addDoc(collection(db, 'messages'), {
                ...contactData,
                created_at: new Date().toISOString(),
                is_read: false,
                type: 'contact'
            });
            return createFakeResponse({ id: docRef.id, ...contactData });
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    getAll: async (params = {}) => {
        try {
            let q = query(collection(db, 'messages'), where('type', '==', 'contact'), orderBy('created_at', 'desc'));

            if (params.unread_only) {
                q = query(q, where('is_read', '==', false));
            }

            const snapshot = await getDocs(q);
            return createFakeResponse(snapshot.docs.map(docToObj));
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    update: async (id, statusData) => {
        try {
            const docRef = doc(db, 'messages', id);
            await updateDoc(docRef, statusData);
            return createFakeResponse({ message: "Updated" });
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },
};

// ==================== APPLICATIONS API ====================
// Placeholder - Assuming similar structure for applications if used

const APPLICATIONS_STORAGE_KEY = 'siteApplications';
const getLocalApplications = () => {
    try {
        const stored = localStorage.getItem(APPLICATIONS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Error reading local applications:', e);
        return [];
    }
};

const saveLocalApplications = (apps) => {
    try {
        localStorage.setItem(APPLICATIONS_STORAGE_KEY, JSON.stringify(apps));
        return true;
    } catch (e) {
        console.error('Error saving local applications:', e);
        return false;
    }
};

export const applicationsAPI = {
    getAll: async (params = {}) => {
        // Implement similar to others if needed
        return createFakeResponse([]);
    },
    getByNumber: async (id) => createFakeResponse({}),
    create: async (data) => {
        const docRef = await addDoc(collection(db, 'applications'), {
            ...data,
            created_at: new Date().toISOString()
        });
        return createFakeResponse({ id: docRef.id, ...data });
    },
    update: async (id, data) => createFakeResponse({}),
    submit: async (id) => createFakeResponse({}),
    updateStatus: async (id, status) => createFakeResponse({}),
};


// ==================== CAREERS API ====================

const CAREERS_STORAGE_KEY = 'siteCareers';

const getLocalCareers = () => {
    try {
        const stored = localStorage.getItem(CAREERS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Error reading local careers:', e);
        return [];
    }
};

const saveLocalCareers = (careers) => {
    try {
        localStorage.setItem(CAREERS_STORAGE_KEY, JSON.stringify(careers));
        return true;
    } catch (e) {
        console.error('Error saving local careers:', e);
        return false;
    }
};

export const careersAPI = {
    getAll: async () => {
        try {
            let allCareers = [];
            try {
                const q = query(collection(db, 'careers'), orderBy('created_at', 'desc'));
                const snapshot = await getDocs(q);
                allCareers = snapshot.docs.map(docToObj);
                saveLocalCareers(allCareers);
                console.log('✅ Loaded', allCareers.length, 'careers from Firestore');
            } catch (firestoreError) {
                console.warn('⚠️ Firestore careers read failed:', firestoreError.message);
                allCareers = getLocalCareers();
            }
            return createFakeResponse(allCareers);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    create: async (data) => {
        try {
            const newCareer = {
                ...data,
                id: Date.now().toString(), // Temp ID for local storage
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            try {
                const docRef = await addDoc(collection(db, 'careers'), {
                    ...data,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                });
                const newDoc = await getDoc(docRef);
                const result = docToObj(newDoc);
                
                // Update local storage with real Firestore ID
                const localCareers = getLocalCareers();
                localCareers.unshift(result);
                saveLocalCareers(localCareers);
                
                return createFakeResponse(result);
            } catch (firestoreError) {
                console.error('❌ Firestore career create failed:', firestoreError.message);
                
                // Save locally even if Firestore fails
                const localCareers = getLocalCareers();
                localCareers.unshift(newCareer);
                saveLocalCareers(localCareers);
                
                // Return original error so UI can handle it
                return createFakeResponse({ detail: firestoreError.message, code: firestoreError.code }, false);
            }
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    update: async (id, data) => {
        try {
            const updateData = {
                ...data,
                updated_at: new Date().toISOString()
            };

            try {
                const docRef = doc(db, 'careers', id);
                await updateDoc(docRef, updateData);
                const updatedDoc = await getDoc(docRef);
                const result = docToObj(updatedDoc);
                
                // Update local
                const localCareers = getLocalCareers();
                const index = localCareers.findIndex(c => c.id === id);
                if (index !== -1) {
                    localCareers[index] = result;
                    saveLocalCareers(localCareers);
                }
                
                return createFakeResponse(result);
            } catch (firestoreError) {
                console.error('❌ Firestore career update failed:', firestoreError.message);
                return createFakeResponse({ detail: firestoreError.message, code: firestoreError.code }, false);
            }
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    delete: async (id) => {
        try {
            try {
                await deleteDoc(doc(db, 'careers', id));
                
                // Update local
                let localCareers = getLocalCareers();
                localCareers = localCareers.filter(c => c.id !== id);
                saveLocalCareers(localCareers);
                
                return createFakeResponse({ message: "Deleted successfully" });
            } catch (firestoreError) {
                console.error('❌ Firestore career delete failed:', firestoreError.message);
                return createFakeResponse({ detail: firestoreError.message, code: firestoreError.code }, false);
            }
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    subscribeToCareers: (callback) => {
        let isSubscribed = true;
        let unsubscribe = () => { };
        let retryCount = 0;
        const MAX_RETRIES = 2;

        const processSnapshot = (docs, source = 'Firebase') => {
            if (!isSubscribed) return;
            try {
                const careers = docs.map(docToObj).filter(Boolean);
                console.log(`📡 Careers loaded from ${source}:`, careers.length);
                saveLocalCareers(careers);
                callback(careers);
                retryCount = 0; // Reset on success
            } catch (err) {
                console.error(`❌ Error processing careers from ${source}:`, err);
            }
        };

        const startListener = (useOrderBy = true) => {
            try {
                const careersRef = collection(db, 'careers');
                const q = useOrderBy 
                    ? query(careersRef, orderBy('created_at', 'desc'))
                    : query(careersRef);

                unsubscribe = onSnapshot(q, (snapshot) => {
                    if (!isSubscribed) return;
                    const source = snapshot.metadata.fromCache ? 'Firebase (Cache)' : 'Firebase (Server)';
                    processSnapshot(snapshot.docs, source);
                }, (error) => {
                    if (!isSubscribed) return;
                    console.warn(`⚠️ Careers subscription issue (orderBy=${useOrderBy}):`, error.code, error.message);
                    
                    // If orderBy failed (likely missing index), try without sort
                    if (useOrderBy && (error.code === 'failed-precondition' || error.message.includes('index'))) {
                        console.log('🔄 Retrying careers subscription without orderBy...');
                        if (unsubscribe) unsubscribe();
                        startListener(false);
                        return;
                    }

                    // Fallback to local storage
                    if (retryCount < MAX_RETRIES) {
                        retryCount++;
                        callback(getLocalCareers());
                    }
                });
            } catch (error) {
                console.error('❌ Failed to setup careers subscription:', error);
                callback(getLocalCareers());
            }
        };

        startListener(true);

        return () => {
            isSubscribed = false;
            if (unsubscribe) unsubscribe();
        };
    }
};

// ==================== CALCULATORS API ====================

// Re-implementing logic from Python backend to client-side JS
export const calculatorsAPI = {
    calculateEMI: async (principal, annualRate, tenureMonths) => {
        // EMI = [P x R x (1+R)^N]/[(1+R)^N-1]
        // R is monthly rate
        const p = parseFloat(principal);
        const r = parseFloat(annualRate) / 100 / 12;
        const n = parseFloat(tenureMonths);

        if (!p || !r || !n) return createFakeResponse({ emi: 0 });

        const x = Math.pow(1 + r, n);
        const emi = (p * r * x) / (x - 1);

        return createFakeResponse({
            emi: Math.round(emi * 100) / 100,
            total_payment: Math.round((emi * n) * 100) / 100,
            total_interest: Math.round(((emi * n) - p) * 100) / 100
        });
    },

    calculateFD: async (principal, annualRate, tenureMonths, compoundFrequency = 'monthly') => {
        // A = P(1 + r/n)^(nt)
        const p = parseFloat(principal);
        const r = parseFloat(annualRate) / 100; // Annual rate as decimal
        const t = parseFloat(tenureMonths) / 12; // Time in years

        let n = 12; // compounding frequency per year
        if (compoundFrequency === 'quarterly') n = 4;
        if (compoundFrequency === 'annually') n = 1;
        if (compoundFrequency === 'maturity') n = 1 / t; // Effectively simple interest if maturity

        const amount = p * Math.pow(1 + (r / n), n * t);
        const interest = amount - p;

        return createFakeResponse({
            maturity_amount: Math.round(amount * 100) / 100,
            interest_earned: Math.round(interest * 100) / 100
        });
    },
};

// ==================== BRANCHES API ====================
// Real-time branch management with Firebase Firestore

const BRANCHES_STORAGE_KEY = 'siteBranches';

// Helper to get branches from localStorage
const getLocalBranches = () => {
    try {
        const stored = localStorage.getItem(BRANCHES_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Error reading local branches:', e);
        return [];
    }
};

// Helper to save branches to localStorage
const saveLocalBranches = (branches) => {
    try {
        localStorage.setItem(BRANCHES_STORAGE_KEY, JSON.stringify(branches));
        return true;
    } catch (e) {
        console.error('Error saving local branches:', e);
        return false;
    }
};

export const branchesAPI = {
    getAll: async () => {
        try {
            let allBranches = [];

            try {
                const q = query(collection(db, 'branches'), orderBy('name', 'asc'));
                const snapshot = await getDocs(q);
                allBranches = snapshot.docs.map(docToObj);
                console.log('✅ Loaded', allBranches.length, 'branches from Firestore');
                saveLocalBranches(allBranches);
            } catch (firestoreError) {
                console.warn('⚠️ Firestore branches read failed:', firestoreError.message);
                allBranches = getLocalBranches();
            }

            return createFakeResponse(allBranches);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    getById: async (id) => {
        try {
            const docRef = doc(db, 'branches', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return createFakeResponse(docToObj(docSnap));
            }
            return createFakeResponse({ detail: 'Branch not found' }, false);
        } catch (error) {
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    create: async (branchData) => {
        try {
            const dataWithTimestamp = {
                ...branchData,
                created_at: Timestamp.now(),
                updated_at: Timestamp.now(),
                // Ensure coordinates are stored as numbers
                coordinates: {
                    lat: parseFloat(branchData.coordinates?.lat || 0),
                    lng: parseFloat(branchData.coordinates?.lng || 0)
                },
                // Ensure phone is an array
                phone: Array.isArray(branchData.phone) ? branchData.phone : [branchData.phone].filter(Boolean),
                // Ensure services is an array
                services: Array.isArray(branchData.services) ? branchData.services : []
            };

            const docRef = await addDoc(collection(db, 'branches'), dataWithTimestamp);
            console.log('✅ Branch created with ID:', docRef.id);

            return createFakeResponse({ id: docRef.id, ...dataWithTimestamp });
        } catch (error) {
            console.error('Error creating branch:', error);
            return createFakeResponse({ detail: error.message }, false);
        }
    },

    update: async (id, branchData) => {
        try {
            // Ensure ID is a string to prevent Firestore TypeError: s.indexOf is not a function
            const docId = String(id);

            // Critical check: If the ID is clearly from mock data (numeric or small integer)
            // or we know we are in a fallback state, we should prevent trying to write to Firestore
            if (!isNaN(docId) && docId.length < 5) {
                return createFakeResponse({ detail: "Cannot update mock data. Please fix Firestore permissions." }, false);
            }

            const docRef = doc(db, 'branches', docId);
            const updateData = {
                ...branchData,
                updated_at: Timestamp.now(),
                // Ensure coordinates are stored as numbers
                coordinates: {
                    lat: parseFloat(branchData.coordinates?.lat || 0),
                    lng: parseFloat(branchData.coordinates?.lng || 0)
                },
                // Ensure phone is an array
                phone: Array.isArray(branchData.phone) ? branchData.phone : [branchData.phone].filter(Boolean),
                // Ensure services is an array
                services: Array.isArray(branchData.services) ? branchData.services : []
            };

            await updateDoc(docRef, updateData);
            console.log('✅ Branch updated:', docId);

            return createFakeResponse({ id: docId, ...updateData });
        } catch (error) {
            console.error('Error updating branch:', error);
            const errorMessage = error.code === 'permission-denied'
                ? "Insufficient permissions. Are you logged in as an admin?"
                : error.message;
            return createFakeResponse({ detail: errorMessage }, false);
        }
    },

    delete: async (id) => {
        try {
            // Ensure ID is a string
            const docId = String(id);

            // Block deleting mock data
            if (!isNaN(docId) && docId.length < 5) {
                return createFakeResponse({ detail: "Cannot delete mock data. Please fix Firestore permissions." }, false);
            }

            const docRef = doc(db, 'branches', docId);
            await deleteDoc(docRef);
            console.log('✅ Branch deleted:', docId);

            return createFakeResponse({ success: true });
        } catch (error) {
            console.error('Error deleting branch:', error);
            const errorMessage = error.code === 'permission-denied'
                ? "Insufficient permissions. Are you logged in as an admin?"
                : error.message;
            return createFakeResponse({ detail: errorMessage }, false);
        }
    },

    // Real-time subscription to branches
    subscribeToBranches: (callback, onError) => {
        let isSubscribed = true;
        let unsubscribe = () => { };

        const processSnapshot = (docs, source = 'Firebase') => {
            if (!isSubscribed) return;
            try {
                const branches = docs.map(docSnap => {
                    const data = docSnap.data();
                    const processedData = { ...data };
                    for (const key in processedData) {
                        if (processedData[key] instanceof Timestamp) {
                            processedData[key] = processedData[key].toDate().toISOString();
                        }
                    }
                    return { ...processedData, id: docSnap.id };
                });

                // Ensure consistent A-Z sorting
                branches.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                
                console.log(`📡 Branches loaded from ${source}:`, branches.length);
                saveLocalBranches(branches);
                callback(branches);
            } catch (err) {
                console.error(`❌ Error processing branches from ${source}:`, err);
            }
        };

        try {
            const branchesRef = collection(db, 'branches');
            
            // Start real-time listener
            // onSnapshot immediately invokes the callback with the current contents
            unsubscribe = onSnapshot(branchesRef, (snapshot) => {
                if (!isSubscribed) return;
                const source = snapshot.metadata.fromCache ? 'Firebase (Cache)' : 'Firebase (Server)';
                processSnapshot(snapshot.docs, source);
            }, (error) => {
                if (!isSubscribed) return;
                console.warn('⚠️ Branches subscription restricted, falling back to LocalStorage:', error.code);
                
                // Fallback to local storage if permission denied or offline
                const localBranches = getLocalBranches();
                console.log('📦 LocalStorage fallback branches:', localBranches.length);
                callback(localBranches);
                if (onError) onError(error);
            });
        } catch (error) {
            console.error('❌ Failed to setup branches subscription:', error);
            callback(getLocalBranches());
        }

        return () => {
            isSubscribed = false;
            if (unsubscribe) unsubscribe();
        };
    }
};

// ==================== HELPER FUNCTIONS ====================

export const parseResponse = async (response) => {
    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.detail || 'An error occurred');
    }

    return data;
};

export class APIError extends Error {
    constructor(message, status, data) {
        super(message);
        this.status = status;
        this.data = data;
    }
}

export default {
    auth: authAPI,
    admin: adminAPI,
    products: productsAPI,
    news: newsAPI,
    ads: adsAPI,
    contact: contactAPI,
    applications: applicationsAPI,
    calculators: calculatorsAPI,
    branches: branchesAPI,
    careers: careersAPI,
};
